import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

/**
 * Product Tenant Context Store
 *
 * Cross-context cache of the currently-viewed product's tenant identity, populated from the product
 * query in useProductInitialization. Lets non-component consumers (e.g. the GoKollab analytics
 * plugin) read product tenant context reactively without coupling to the UI store.
 */
export const useProductTenantContextStore = defineStore('product-tenant-context', () => {
  const productId = ref('')
  const productLocationId = ref('')
  const isLoading = ref(false)

  const setContext = (context: { productId: string; productLocationId: string; isLoading: boolean }) => {
    if (productId.value === context.productId && productLocationId.value && !context.productLocationId) {
      isLoading.value = context.isLoading
      return
    }
    productId.value = context.productId
    productLocationId.value = context.productLocationId
    isLoading.value = context.isLoading
  }

  const reset = () => {
    productId.value = ''
    productLocationId.value = ''
    isLoading.value = false
  }

  const resetIfProduct = (expectedProductId: string, expectedLocationId?: string) => {
    if (productId.value !== expectedProductId) return
    if (expectedLocationId && productLocationId.value !== expectedLocationId) return
    reset()
  }

  return {
    productId: computed(() => productId.value),
    productLocationId: computed(() => productLocationId.value),
    isLoading: computed(() => isLoading.value),
    setContext,
    reset,
    resetIfProduct,
  }
})
